import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import type { ApprovalThreshold, PendingApproval } from "../../types";

const { Title, Text } = Typography;

const ApprovalsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PendingApproval[]>([]);
  const [thresholds, setThresholds] = useState<ApprovalThreshold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [target, setTarget] = useState<{ row: PendingApproval; mode: "approve" | "reject" } | null>(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeService.getPendingApprovals();
      setRows(res.rows || []);
      setThresholds(res.thresholds || []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Failed to load pending approvals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!target) return;
    setBusyId(target.row.id);
    try {
      if (target.mode === "approve") {
        await financeService.approveVoucher(target.row.id, notes.trim() || undefined);
        message.success("Approved and posted to the ledger");
      } else {
        await financeService.rejectVoucher(target.row.id, notes.trim());
        message.success("Voucher rejected");
      }
      setTarget(null);
      setNotes("");
      await load();
    } catch (e: any) {
      message.error(e?.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      title: "Voucher",
      dataIndex: "voucher_no",
      key: "voucher_no",
      width: 150,
      render: (v: string, row: PendingApproval) => (
        <a onClick={() => navigate(`/finance/vouchers/${row.id}`)}>{v}</a>
      ),
    },
    {
      title: "Date",
      dataIndex: "business_date",
      key: "business_date",
      width: 110,
      render: (v: string) => dayjs(v).format("MMM D, YYYY"),
    },
    {
      title: "Type",
      dataIndex: "voucher_type",
      key: "voucher_type",
      width: 90,
      render: (v: string) => <Tag color={v === "income" ? "green" : "volcano"}>{v}</Tag>,
    },
    { title: "Department", dataIndex: "department_name", key: "dept", width: 160, ellipsis: true },
    { title: "Description", dataIndex: "description", key: "description", ellipsis: true },
    { title: "Requested by", dataIndex: "created_by_name", key: "by", width: 130, ellipsis: true },
    {
      title: "Amount",
      key: "amount",
      width: 140,
      align: "right" as const,
      render: (_: any, row: PendingApproval) => <Text strong>{formatLkr(row.amount + row.tax)}</Text>,
    },
    {
      title: "Approvals",
      key: "approvals",
      width: 110,
      render: (_: any, row: PendingApproval) => (
        <Tag color="processing">
          {row.approvals_count}/{row.approvals_required}
        </Tag>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 190,
      render: (_: any, row: PendingApproval) => (
        <Space size={4}>
          <Button size="small" onClick={() => navigate(`/finance/vouchers/${row.id}`)}>
            Review
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={() => {
              setNotes("");
              setTarget({ row, mode: "approve" });
            }}
          >
            Approve
          </Button>
          <Button
            size="small"
            danger
            onClick={() => {
              setNotes("");
              setTarget({ row, mode: "reject" });
            }}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Approvals
          </Title>
          <Text type="secondary">
            {loading ? "Loading…" : `${rows.length} voucher(s) waiting`}
          </Text>
        </div>
        <Button onClick={load}>Refresh</Button>
      </Space>

      {thresholds.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="Approval thresholds"
          description={
            <span>
              {thresholds.map((t, i) => (
                <span key={i}>
                  {i > 0 && " · "}
                  <strong>{t.approvals}</strong> distinct approver{t.approvals > 1 ? "s" : ""}{" "}
                  {t.max === null ? "above the previous band" : `up to ${formatLkr(t.max)}`}
                </span>
              ))}
            </span>
          }
        />
      )}

      {error && (
        <Alert type="error" showIcon title="Failed to load" description={error} style={{ marginBottom: 16 }} />
      )}

      <Card size="small">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1100 }}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing waiting for your approval" />
            ),
          }}
        />
      </Card>

      <Modal
        open={!!target}
        title={target?.mode === "approve" ? "Approve voucher" : "Reject voucher"}
        okText={target?.mode === "approve" ? "Approve" : "Reject"}
        okButtonProps={{ danger: target?.mode === "reject", disabled: target?.mode === "reject" && !notes.trim() }}
        confirmLoading={!!busyId}
        onCancel={() => setTarget(null)}
        onOk={submit}
      >
        {target && (
          <>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Voucher">{target.row.voucher_no}</Descriptions.Item>
              <Descriptions.Item label="Amount">
                <Text strong>{formatLkr(target.row.amount + target.row.tax)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Description">{target.row.description}</Descriptions.Item>
              <Descriptions.Item label="Approvals so far">
                {target.row.approvals_count} of {target.row.approvals_required} required
              </Descriptions.Item>
            </Descriptions>
            <Input.TextArea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                target.mode === "approve" ? "Notes (optional)" : "Reason for rejecting (required)"
              }
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default ApprovalsPage;
